"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useInternalPaginatedBulkQuery = void 0;
var React = require("react");
var savant_listing_1 = require("savant-listing");
var store_1 = require("../../../store");
var utils_1 = require("../../../utils");
var response_1 = require("../../response");
var variant_1 = require("../../variant");
var dependency_1 = require("../dependency");
var queryFunctions_1 = require("../queryFunctions");
var useCurrentDependencies_1 = require("../useCurrentDependencies");
var useQueryLifecycle_1 = require("../useQueryLifecycle");
var defaultMergePages_1 = require("./defaultMergePages");
var getResult = function (dependency) {
    return (dependency &&
        dependency.currentState &&
        dependency.currentState.variant &&
        dependency.currentState.variant.result);
};
var getNextPageTokenFromDependency = function (dependency) {
    var result = getResult(dependency);
    return result && dependency_1.getNextPageToken(result.response);
};
var getListResponseDataFromDependency = function (dependency) {
    var result = getResult(dependency);
    return response_1.getListResponseData(result);
};
function useInternalPaginatedBulkQuery(_a) {
    var options = _a.options, method = _a.method, wait = _a.wait, getTrailingDependency = _a.getTrailingDependency, mergeDependencies = _a.mergeDependencies;
    var store = store_1.useStoreContext().store;
    var currentDependencies = useCurrentDependencies_1.default(method, options);
    var _b = options.mergePages, mergePages = _b === void 0 ? defaultMergePages_1.default : _b;
    // if any of our deps have an error state we're in an error state
    var error = React.useMemo(function () {
        var inError = currentDependencies.find(function (_a) {
            var status = _a.currentState.variant.status;
            return status === variant_1.Status.Error;
        });
        return inError && inError.currentState.variant.error;
    }, [currentDependencies]);
    // We're in a loading state iff we're not in error, and any of
    // our statuses are not success
    var loading = React.useMemo(function () {
        if (wait) {
            return true;
        }
        if (currentDependencies.length === 0) {
            return false;
        }
        return (!error &&
            !currentDependencies.every(function (_a) {
                var status = _a.currentState.variant.status;
                return status === variant_1.Status.Success;
            }));
    }, [currentDependencies, error, wait]);
    // We need to have the first page fetched from all of our queries
    // in order to avoid shuffling of results as new data comes in. As long
    // as the variant has a result, we've fetched at least one page
    var firstPageLoaded = React.useMemo(function () {
        return currentDependencies.every(function (_a) {
            var result = _a.currentState.variant.result;
            return result !== undefined;
        });
    }, [currentDependencies]);
    // it is possible for an api to return a list of results, incorrectly sorted, relative to the comparator
    // order that was provided by the consumer. This could be user error, or api error. In both cases,
    // to protect from this we will resort the data after receiving. This could cause data in error to appear
    // at the end of pages, instead of being dropped.
    var reorderedDependencies = React.useMemo(function () {
        if (!error &&
            options.comparator &&
            currentDependencies &&
            firstPageLoaded) {
            return currentDependencies.map(function (dependency) {
                var result = getResult(dependency);
                var data = getListResponseDataFromDependency(dependency);
                if (result) {
                    var sorted = savant_listing_1.stableSort(data, options.comparator);
                    if (!utils_1.shallowEqual(sorted, data)) {
                        // TODO: allow the connector to use a metrics tool or a logger to emit something useful.
                        // https://jira.oci.oraclecorp.com/browse/CSDK-892
                        console.error("API Request responded with improperly sorted data according to the comparator");
                        var sortedData = sorted;
                        if (queryFunctions_1.hasItemsArray(data)) {
                            var _ = data.items, dataRest = __rest(data, ["items"]);
                            sortedData = __assign({ items: sorted }, dataRest);
                        }
                        var copy = JSON.parse(JSON.stringify(dependency));
                        copy.currentState.variant.result.data = sortedData;
                        return copy;
                    }
                }
                return dependency;
            });
        }
        else {
            return currentDependencies;
        }
    }, [currentDependencies, error, options, firstPageLoaded]);
    // Get the dependency with the 'leftmost' (w.r.t. the comparator) item as it's last
    // loaded item.  This item acts as the largest (or rightmost) value we return in
    // our merged data, as well as indicates the next dependency to page when we call
    // nextPage
    var trailingDependency = React.useMemo(function () {
        if (!firstPageLoaded || reorderedDependencies.length === 0) {
            return undefined;
        }
        return getTrailingDependency(reorderedDependencies);
    }, [getTrailingDependency, firstPageLoaded, reorderedDependencies]);
    // The response is a merged (using provided mergePages option) view
    // of all of our dependencies
    var response = React.useMemo(function () {
        if (!trailingDependency) {
            return undefined;
        }
        return mergeDependencies(reorderedDependencies, trailingDependency);
    }, [mergeDependencies, reorderedDependencies, trailingDependency]);
    var nextPage = React.useMemo(function () {
        var nextPageToken = getNextPageTokenFromDependency(trailingDependency);
        if (loading || !nextPageToken) {
            return undefined;
        }
        return function () {
            return dependency_1.callPaginatedDependency(trailingDependency, store, mergePages, nextPageToken);
        };
    }, [loading, store, trailingDependency, mergePages]);
    var call = React.useCallback(function (dependency) {
        dependency_1.callPaginatedDependency(dependency, store, mergePages);
    }, [store, mergePages]);
    var refresh = useQueryLifecycle_1.default({
        call: call,
        wait: wait,
        dependencies: currentDependencies,
    });
    return {
        loading: loading,
        error: error,
        nextPage: nextPage,
        refresh: refresh,
        response: response,
    };
}
exports.useInternalPaginatedBulkQuery = useInternalPaginatedBulkQuery;
function isResponseArray(resp) {
    return Array.isArray(resp);
}
function usePaginatedBulkQuery(_a) {
    var _b = _a.options, comparator = _b.comparator, _c = _b.mergePages, mergePages = _c === void 0 ? defaultMergePages_1.default : _c, optionsRest = __rest(_b, ["comparator", "mergePages"]), rest = __rest(_a, ["options"]);
    var getTrailingDependency = React.useCallback(function (dependencies) {
        // For a bulk query, the furthest behind dependency - the one that sets the 'rightmost' (w.r.t. the comparator)
        // during the results merge, and the one that is next to be paged - is determined very differently if there
        // any dependencies with a next page, and if there aren't.
        var dependenciesWithNextPage = dependencies.filter(function (state) {
            return getNextPageTokenFromDependency(state);
        });
        // If there aren't any dependencies with a next page (all data is fetched) we need to find the dependency
        // with the overall 'rightmost' data in it's last item.  In other-words the largest item in the entire result
        // set.  'nulls' (dependencies with empty data arrays should be first)
        var candidates = dependencies;
        var baseOrder = savant_listing_1.Ordering.fromComparator(comparator).nullsFirst();
        if (dependenciesWithNextPage.length > 0) {
            // If there *are* dependencies with a next page, we need to search only those for the dependency with
            // the 'leftmost' value as it's last item.  We don't want to merge any items bigger than this, or we
            // may end up having item 'pop-in' when we fetch the next page for this dependency.  'nulls' in this
            // case probably shouldn't exist, but if they do, they should be last (which is taken care of with
            // the reverse).
            baseOrder = baseOrder.reverse();
            candidates = dependenciesWithNextPage;
        }
        var order = baseOrder.fromMappedField(function (dep) {
            return utils_1.lastItem(getListResponseDataFromDependency(dep));
        });
        return utils_1.lastItem(savant_listing_1.stableSort(candidates, order));
    }, [comparator]);
    // Merge dependencies together using the same callback used to merge pages together
    // Filter out anything 'right' of the last item in the trailing dependency, and
    // sort the merged result using the provided comparator.
    var merge = React.useCallback(function (dependencies, trailingDependency) {
        var trailingItem = utils_1.lastItem(getListResponseDataFromDependency(trailingDependency));
        var _a = __read(dependencies), first = _a[0], remaining = _a.slice(1);
        var merged = remaining.reduce(function (acc, dep) {
            return mergePages(acc, dep.currentState.variant.result);
        }, first.currentState.variant.result);
        var filteredData = response_1.getListResponseData(merged).filter(function (item) { return comparator(item, trailingItem) <= 0; });
        var sortedData = savant_listing_1.stableSort(filteredData, comparator);
        if (isResponseArray(merged.data)) {
            return Object.assign(merged, { data: sortedData });
        }
        return Object.assign(merged, {
            data: __assign(__assign({}, merged.data), { items: sortedData }),
        });
    }, [mergePages, comparator]);
    return useInternalPaginatedBulkQuery(__assign({ getTrailingDependency: getTrailingDependency, mergeDependencies: merge, options: __assign({ mergePages: mergePages, comparator: comparator }, optionsRest) }, rest));
}
exports.default = usePaginatedBulkQuery;
//# sourceMappingURL=usePaginatedBulkQuery.js.map